import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, Grid, FormControl, FormLabel, Input, Text, Alert, AlertIcon, Box,
} from '@chakra-ui/react';
import { createRequest, errorMessage, DEFAULT_AMOUNTS } from 'api/administrador/documentRequests';
import StudentPicker from './StudentPicker';
import RequestFields, { emptyValues, bodyFromValues } from './RequestFields';

/**
 * Registrar a mano una solicitud (lo que hoy se anota en el Drive).
 * Si se liga a un estudiante, la API completa nombre, documento y contacto desde su ficha.
 */
export default function NewRequestModal({ isOpen, onClose, options, onCreated, initialStudent = null }) {
  const amounts = options?.defaultAmounts || DEFAULT_AMOUNTS;
  const [values, setValues] = useState(() => emptyValues(amounts.digital));
  const [student, setStudent] = useState(initialStudent);
  const [requester, setRequester] = useState({ name: '', document: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(emptyValues(amounts.digital));
      setStudent(initialStudent);
      setRequester({ name: '', document: '' });
      setError('');
    }
  }, [isOpen, initialStudent, amounts.digital]);

  const onChange = (name, value) => setValues((v) => {
    const next = { ...v, [name]: value };
    // Si el valor sigue siendo la tarifa sugerida, se ajusta al cambiar el método de envío.
    if (name === 'deliveryMethod' && String(v.amount) === String(amounts[v.deliveryMethod])) {
      next.amount = amounts[value] ?? v.amount;
    }
    return next;
  });

  const pickStudent = (s) => {
    setStudent(s);
    if (s) {
      setValues((v) => ({ ...v, phone: v.phone || s.cellphone || '', email: v.email || s.email || '' }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const created = await createRequest({
        ...bodyFromValues(values),
        studentId: student?.id || null,
        requesterName: student ? undefined : requester.name,
        requesterDocument: student ? undefined : requester.document,
      });
      if (onCreated) onCreated(created);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={submit} borderRadius="16px" noValidate>
        <ModalHeader fontSize="18px" pb={1}>Nueva solicitud</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Recibe un número consecutivo al guardarla. Si la persona es estudiante, lígala para tener su ficha a mano.
          </Text>
          {error && (
            <Alert status="error" borderRadius="8px" mb={4} fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Box mb={4}>
            <Text fontSize="12px" fontWeight="600" letterSpacing=".06em" textTransform="uppercase" color="gray.500" mb={2}>Quién la pide</Text>
            <StudentPicker value={student} onChange={pickStudent} inputId="dr-new-student" />
            {!student && (
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={3} mt={3}>
                <FormControl>
                  <FormLabel htmlFor="dr-new-name" fontSize="13px" color="gray.600" mb={1}>Nombre (si no es estudiante)</FormLabel>
                  <Input
                    id="dr-new-name" size="sm" borderRadius="8px" value={requester.name}
                    onChange={(e) => setRequester((r) => ({ ...r, name: e.target.value }))}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="dr-new-doc" fontSize="13px" color="gray.600" mb={1}>Documento</FormLabel>
                  <Input
                    id="dr-new-doc" size="sm" borderRadius="8px" value={requester.document}
                    onChange={(e) => setRequester((r) => ({ ...r, document: e.target.value }))}
                  />
                </FormControl>
              </Grid>
            )}
          </Box>

          <RequestFields values={values} onChange={onChange} options={options} idPrefix="dr-new" />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={busy}>Cancelar</Button>
          <Button type="submit" bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} isLoading={busy}>Crear solicitud</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
